import { PanelBase } from "./PanelBase";
import type { GameContext } from "../../GameContext";

interface AvailableJob {
  jobId: string;
  name: string;
  tier: 1 | 2 | 3;
  eligible: boolean;
  requirementSummary: string;
}

/**
 * Per 1.5: the three named trial quests -- "Trial of the Path" (Base
 * Level 10 -> Tier 1, choose 1 of 4), "Awakening" (Base Level 40 -> Tier
 * 2, 1 of 2 branches), "Ascension" (Base Level 70 -> Tier 3, requires a
 * raid clear + World Boss drop) -- are the authoritative gate for
 * job-change eligibility (per M04's spec section, "Team Wanderer (M04)
 * ... build against these exact names and level thresholds"). This panel
 * never computes eligibility itself; it only reflects what GET
 * /api/v1/jobs returns and sends the chosen job to POST
 * /api/v1/characters/:id/job-change.
 */
export class JobAdvancementPanel extends PanelBase {
  constructor(root: HTMLElement, ctx: GameContext, private readonly characterId: string) {
    super(root, ctx, "job-advancement-panel");
  }

  protected async refresh(): Promise<void> {
    this.el.innerHTML = "<p>Loading available jobs…</p>";
    const result = await this.ctx.network.rest.get<AvailableJob[]>(
      `/api/v1/jobs?characterId=${this.characterId}`
    );

    if (!result.ok) {
      this.el.innerHTML = `<p class="panel-error">${escapeHtml(result.message)}</p>`;
      return;
    }

    this.render(result.data);
  }

  private render(jobs: AvailableJob[]): void {
    this.el.innerHTML = "<h3>Job Advancement</h3>";

    if (jobs.length === 0) {
      const p = document.createElement("p");
      p.textContent = "No job change currently available. Complete the next trial quest to unlock one.";
      this.el.appendChild(p);
      return;
    }

    const list = document.createElement("div");
    list.className = "job-list";

    for (const job of jobs) {
      const row = document.createElement("div");
      row.className = "job-row";
      row.innerHTML = `
        <div class="job-name">${escapeHtml(job.name)} (Tier ${job.tier})</div>
        <div class="job-requirement">${escapeHtml(job.requirementSummary)}</div>
      `;

      const changeBtn = document.createElement("button");
      changeBtn.textContent = "Advance";
      changeBtn.disabled = !job.eligible;
      changeBtn.addEventListener("click", () => void this.changeJob(job.jobId));
      row.appendChild(changeBtn);

      list.appendChild(row);
    }
    this.el.appendChild(list);
  }

  private async changeJob(jobId: string): Promise<void> {
    const result = await this.ctx.network.rest.post(
      `/api/v1/characters/${this.characterId}/job-change`,
      { jobId }
    );
    if (result.ok) await this.refresh();
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
